import {namesOf} from '@/utils/names';
import {RIGHTS} from '@/views/roles/rights';

/**
 * The contact person of a vacancy is the user responsible for it. We keep their name and mobile number on the
 * vacancy itself, so the number travels with the vacancy (for example to the website).
 */
export function contactPersonOf(user) {
    if (!user) return null;

    return {
        id: user.id,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        mobile: user.mobile || '',
    };
}

export function contactPersonName(person) {
    return person ? `${person.firstname || ''} ${person.lastname || ''}`.trim() : '';
}

/**
 * Only Workfix employees with access to the department can be the contact person: an active account with a role in
 * that department, and not a customer role.
 */
export function workfixEmployeesOf(users, roles, departmentName) {
    const rolesById = {};
    (roles || []).forEach(role => {
        if (role) rolesById[role.id] = role;
    });

    return (users || []).filter(user => {
        if (!user || user.disabled) return false;

        const role = rolesById[user.roles && user.roles[departmentName]];

        return Boolean(role) && !namesOf(role.rights).includes(RIGHTS.IS_CUSTOMER);
    });
}

/**
 * The logged-in user as contact person of a vacancy they register, but only when they could also be chosen: a
 * Workfix employee with access to their selected department.
 */
export function ownContactPerson(me, roles) {
    if (!me) return null;

    return contactPersonOf(workfixEmployeesOf([me], roles, me.selectedDepartment)[0]);
}

export function contactPersonOptions(employees) {
    return (employees || [])
        .map(contactPersonOf)
        .sort((a, b) => contactPersonName(a).localeCompare(contactPersonName(b)));
}

/**
 * By default the user who registered the vacancy is responsible for it, as long as they are still one of the
 * employees who can be chosen. The creator stored on a vacancy has no mobile number; the employees list does.
 */
export function defaultContactPerson(employees, creator) {
    if (!creator) return null;

    return contactPersonOf((employees || []).find(employee => employee.id === creator.id));
}
